import { Link } from "react-router-dom";
import { trackEvent } from "../lib/tracking";

export function CardMini({ card, articleId }) {
  if (!card) return null;
  return (
    <div className="editorial-card p-6" data-testid={`card-mini-${card.id}`}>
      <div className="grid grid-cols-5 gap-4 items-start">
        <div className="col-span-2">
          <div className="aspect-[1.58/1] bg-[#EFECE5] overflow-hidden border border-[#D6CFC4]">
            {card.card_image && <img src={card.card_image} alt={card.card_name} className="w-full h-full object-cover" loading="lazy" />}
          </div>
        </div>
        <div className="col-span-3">
          {card.badge_text && <div className="overline text-[#B4533C] mb-1">{card.badge_text}</div>}
          <div className="font-serif text-xl leading-tight">{card.card_name}</div>
          <div className="text-xs text-[#4A4742] mt-1">{card.issuer}</div>
        </div>
      </div>
      <div className="grid grid-cols-3 grid-borders mt-5 border-t border-l border-[#D6CFC4]">
        <div className="p-3">
          <div className="overline text-[#4A4742]">Annual Fee</div>
          <div className="font-mono text-sm mt-1">${card.annual_fee}</div>
        </div>
        <div className="p-3">
          <div className="overline text-[#4A4742]">Bonus</div>
          <div className="font-mono text-sm mt-1 leading-tight">{card.welcome_bonus?.split(" ").slice(0, 3).join(" ")}…</div>
        </div>
        <div className="p-3">
          <div className="overline text-[#4A4742]">Rating</div>
          <div className="font-mono text-sm mt-1">{card.editorial_rating?.toFixed(1)}/5</div>
        </div>
      </div>
      <a
        href={card.affiliate_url}
        target="_blank"
        rel="sponsored noopener noreferrer"
        onClick={() => trackEvent({
          event_type: "card_click",
          card_id: card.id,
          card_name: card.card_name,
          issuer: card.issuer,
          article_id: articleId,
        })}
        className="mt-5 block text-center bg-[#1B3B36] text-white py-3 text-sm tracking-wide hover:bg-[#112622] transition-colors"
        data-testid={`card-apply-${card.id}`}
      >
        {card.apply_button_text || "Apply Now"}
      </a>
      <div className="mt-3 text-[10px] text-[#4A4742] leading-snug font-mono">{card.disclosure_text}</div>
    </div>
  );
}

export function SingleCardCTA({ card, heading, articleId }) {
  return (
    <section className="my-10" data-testid="single-card-cta">
      {heading && <h3 className="font-serif text-2xl mb-4">{heading}</h3>}
      <CardMini card={card} articleId={articleId} />
    </section>
  );
}

export function TwoCardComparison({ cards, heading = "Head to head", articleId }) {
  const [a, b] = cards || [];
  return (
    <section className="my-10" data-testid="two-card-comparison">
      <div className="overline text-[#B4533C] mb-2">Comparison</div>
      <h3 className="font-serif text-3xl mb-6">{heading}</h3>
      <div className="grid md:grid-cols-2 gap-6">
        {a && <CardMini card={a} articleId={articleId} />}
        {b && <CardMini card={b} articleId={articleId} />}
      </div>
    </section>
  );
}

export function ThreeCardBestFor({ cards, heading = "Editors' top three", articleId }) {
  return (
    <section className="my-12" data-testid="three-card-best-for">
      <div className="overline text-[#B4533C] mb-2">Best For Module</div>
      <h3 className="font-serif text-3xl mb-6">{heading}</h3>
      <div className="grid md:grid-cols-3 gap-5">
        {cards?.slice(0, 3).map((c) => (
          <CardMini key={c.id} card={c} articleId={articleId} />
        ))}
      </div>
    </section>
  );
}

export function InlineTextCTA({ card, caption, articleId }) {
  if (!card) return null;
  return (
    <div className="my-6 border-l-4 border-[#1B3B36] pl-4 py-2 bg-[#EFECE5]/40" data-testid="inline-text-cta">
      <div className="overline text-[#B4533C]">Sponsored placement</div>
      <div className="mt-1 text-sm">
        {caption || `Consider the `}
        <a
          href={card.affiliate_url}
          target="_blank"
          rel="sponsored noopener noreferrer"
          onClick={() => trackEvent({ event_type: "card_click", card_id: card.id, card_name: card.card_name, issuer: card.issuer, article_id: articleId })}
          className="font-medium underline"
        >
          {card.card_name}
        </a>
        {` — ${card.welcome_bonus}.`}
      </div>
    </div>
  );
}

export function StickyMobileCardCTA({ card, articleId }) {
  if (!card) return null;
  return (
    <div className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-[#D6CFC4] px-4 py-3 flex items-center justify-between gap-3" data-testid="sticky-mobile-cta">
      <div className="min-w-0 flex-1">
        <div className="overline text-[#B4533C] text-[10px]">Featured Card</div>
        <div className="text-sm font-medium truncate">{card.card_name}</div>
      </div>
      <a
        href={card.affiliate_url}
        target="_blank"
        rel="sponsored noopener noreferrer"
        onClick={() => trackEvent({ event_type: "card_click", card_id: card.id, card_name: card.card_name, issuer: card.issuer, article_id: articleId })}
        className="shrink-0 bg-[#1B3B36] text-white text-xs tracking-wide px-4 py-2 hover:bg-[#112622]"
        data-testid="sticky-mobile-apply"
      >
        Apply
      </a>
    </div>
  );
}

export function EndOfArticleBestCards({ cards, articleId }) {
  return (
    <section className="my-14 border-t border-b border-[#1C1A17] py-10" data-testid="end-of-article-best">
      <div className="overline text-[#B4533C] mb-2">Before you go</div>
      <h3 className="font-serif text-3xl md:text-4xl tracking-tight mb-6">The best cards for readers like you</h3>
      <div className="grid md:grid-cols-3 gap-5">
        {cards?.slice(0, 3).map((c) => <CardMini key={c.id} card={c} articleId={articleId} />)}
      </div>
    </section>
  );
}

export function renderAffiliateBlock(block, cardsById, articleId) {
  const cards = (block.card_ids || []).map((id) => cardsById[id]).filter(Boolean);
  switch (block.block_type) {
    case "single_card_cta":
      return <SingleCardCTA card={cards[0]} heading={block.heading} articleId={articleId} />;
    case "two_card_comparison":
      return <TwoCardComparison cards={cards} heading={block.heading} articleId={articleId} />;
    case "three_card_best_for":
      return <ThreeCardBestFor cards={cards} heading={block.heading} articleId={articleId} />;
    case "inline_text_cta":
      return <InlineTextCTA card={cards[0]} caption={block.caption} articleId={articleId} />;
    case "sticky_mobile_card":
      return <StickyMobileCardCTA card={cards[0]} articleId={articleId} />;
    case "end_of_article_best_cards":
      return <EndOfArticleBestCards cards={cards} articleId={articleId} />;
    default:
      return null;
  }
}
